/**
 * Scout v0 - criteria composer TanStack Query hooks (P16).
 *
 * Mirrors the P14/P15 backend compose flow:
 *   compose -> propose -> confirm -> preview -> launch
 *
 * Backend enforces "no launch without confirmed loose roles" (returns 409 with
 * unconfirmedRoles). The UI must additionally NOT offer a launch path that
 * bypasses the preview screen - the launch mutation is only invoked from the
 * preview step.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiV2Fetch } from './api';
import {
  SCOUT_COMPOSE_CONFIRM_ENDPOINT,
  SCOUT_COMPOSE_ENDPOINT,
  SCOUT_COMPOSE_LAUNCH_ENDPOINT,
  SCOUT_COMPOSE_PREVIEW_ENDPOINT,
  SCOUT_COMPOSE_PROPOSE_ENDPOINT,
  SCOUT_CRITERIA_PROFILE_ENDPOINT,
  SCOUT_RUNS_ENDPOINT,
  SCOUT_SUPPRESSIONS_ADD_ENDPOINT,
  SCOUT_SUPPRESSIONS_ENDPOINT,
  SCOUT_SUPPRESSIONS_REMOVE_ENDPOINT,
  SCOUT_TEMPLATES_ENDPOINT,
  scoutExportGenerateEndpoint,
  scoutExportHistoryEndpoint,
  scoutExportPreviewEndpoint,
  scoutQaDecisionEndpoint,
  scoutQaQueueEndpoint,
  scoutQaSummaryEndpoint,
  scoutRunDetailEndpoint,
  scoutRunResumeEndpoint,
  scoutSuppressionPromoteEndpoint,
  scoutSuppressionTypeEndpoint,
  scoutTemplateArchiveEndpoint,
  scoutTemplateDetailEndpoint,
} from './api-contract';
import type { SuppressionListType } from './api-contract';
import type {
  ClassTemplate,
  ComposeRequest,
  ComposeResponse,
  ConfirmResponse,
  ConfirmedRoleEdit,
  CriteriaProfile,
  ExportGenerateResponse,
  ExportHistoryResponse,
  ExportPreviewResponse,
  ExportScope,
  LaunchRequest,
  LaunchResponse,
  PreviewResponse,
  PromotedSuppression,
  ProposeResponse,
  QaDecisionRequest,
  QaDecisionResponse,
  QaQueueResponse,
  QaSessionSummaryResponse,
  RoleSpec,
  RunDetail,
  RunSummary,
  RunsListResponse,
  SuppressionAddRequest,
  SuppressionClearResponse,
  SuppressionLists,
  SuppressionRemoveRequest,
  SuppressionRemoveResponse,
  SuppressionReplaceRequest,
} from '@/types/scout';

/* ─────────────────────────────────────────────────────────────────────── */
/* Query keys                                                               */
/* ─────────────────────────────────────────────────────────────────────── */

export const scoutQueryKeys = {
  profile: ['scout', 'profile'] as const,
  templates: ['scout', 'templates'] as const,
  template: (id: string) => ['scout', 'templates', id] as const,
  runs: ['scout', 'runs'] as const,
  run: (id: string) => ['scout', 'runs', id] as const,
  qaQueue: (runId: string) => ['scout', 'qa', runId, 'queue'] as const,
  qaSummary: (runId: string) => ['scout', 'qa', runId, 'summary'] as const,
  exportPreview: (runId: string, scope: ExportScope) => ['scout', 'export', runId, 'preview', scope] as const,
  exportHistory: (runId: string) => ['scout', 'export', runId, 'history'] as const,
  suppressions: ['scout', 'suppressions'] as const,
};

/* ─────────────────────────────────────────────────────────────────────── */
/* Layer 1 - workspace criteria profile (read-only in the composer)        */
/* ─────────────────────────────────────────────────────────────────────── */

export function useScoutProfile() {
  return useQuery<CriteriaProfile, ApiError>({
    queryKey: scoutQueryKeys.profile,
    queryFn: () => apiV2Fetch<CriteriaProfile>(SCOUT_CRITERIA_PROFILE_ENDPOINT),
  });
}

interface UpdateProfilePayload {
  identity: string;
}

/**
 * Update the workspace identity. Sends identity only — the backend PUT
 * preserves the stored suppression lists when the field is omitted, so this
 * never races the typed-list suppression mutations.
 */
export function useUpdateScoutProfile() {
  const queryClient = useQueryClient();
  return useMutation<CriteriaProfile, ApiError, UpdateProfilePayload>({
    mutationFn: (payload) =>
      apiV2Fetch<CriteriaProfile>(SCOUT_CRITERIA_PROFILE_ENDPOINT, {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData<CriteriaProfile>(scoutQueryKeys.profile, updated);
    },
  });
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Layer 2 - class templates (CRUD)                                        */
/* ─────────────────────────────────────────────────────────────────────── */

export function useScoutTemplates() {
  return useQuery<ClassTemplate[], ApiError>({
    queryKey: scoutQueryKeys.templates,
    queryFn: () => apiV2Fetch<ClassTemplate[]>(SCOUT_TEMPLATES_ENDPOINT),
  });
}

interface CreateTemplatePayload {
  name: string;
  archetype?: string;
  roles?: RoleSpec[];
  keywords?: string[];
}

export function useCreateScoutTemplate() {
  const queryClient = useQueryClient();
  return useMutation<ClassTemplate, ApiError, CreateTemplatePayload>({
    mutationFn: (payload) =>
      apiV2Fetch<ClassTemplate>(SCOUT_TEMPLATES_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: (created) => {
      // Optimistic: append the new template to the cached list.
      queryClient.setQueryData<ClassTemplate[]>(scoutQueryKeys.templates, (old) =>
        old ? [...old, created] : [created],
      );
    },
  });
}

interface UpdateTemplatePayload {
  templateId: string;
  name?: string;
  archetype?: string;
  roles?: RoleSpec[];
  keywords?: string[];
}

export function useUpdateScoutTemplate() {
  const queryClient = useQueryClient();
  return useMutation<ClassTemplate, ApiError, UpdateTemplatePayload>({
    mutationFn: ({ templateId, ...payload }) =>
      apiV2Fetch<ClassTemplate>(scoutTemplateDetailEndpoint(templateId), {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData<ClassTemplate[]>(scoutQueryKeys.templates, (old) =>
        old ? old.map((t) => (t.id === updated.id ? updated : t)) : [updated],
      );
    },
  });
}

export function useArchiveScoutTemplate() {
  const queryClient = useQueryClient();
  return useMutation<ClassTemplate, ApiError, string>({
    mutationFn: (templateId) =>
      apiV2Fetch<ClassTemplate>(scoutTemplateArchiveEndpoint(templateId), {
        method: 'POST',
      }),
    onSuccess: (archived) => {
      // Remove the archived template from the active list.
      queryClient.setQueryData<ClassTemplate[]>(scoutQueryKeys.templates, (old) =>
        old ? old.filter((t) => t.id !== archived.id) : [],
      );
    },
  });
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Compose flow                                                             */
/* ─────────────────────────────────────────────────────────────────────── */

/** POST /scout/compose - merge layer 1 + template + specifics (no AI). */
export function useCompose() {
  return useMutation<ComposeResponse, ApiError, ComposeRequest>({
    mutationFn: (payload) =>
      apiV2Fetch<ComposeResponse>(SCOUT_COMPOSE_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
  });
}

/** POST /scout/compose/propose - AI synonym proposals for loose roles. */
export function usePropose() {
  return useMutation<ProposeResponse, ApiError, { composed: ComposeResponse['composed'] }>({
    mutationFn: (payload) =>
      apiV2Fetch<ProposeResponse>(SCOUT_COMPOSE_PROPOSE_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify({ composed: payload.composed }),
      }),
  });
}

interface ConfirmPayload {
  composed: ComposeResponse['composed'];
  edits: ConfirmedRoleEdit[];
  templateId: string;
}

/** POST /scout/compose/confirm - apply confirmed synonyms + persist to template. */
export function useConfirm() {
  return useMutation<ConfirmResponse, ApiError, ConfirmPayload>({
    mutationFn: (payload) =>
      apiV2Fetch<ConfirmResponse>(SCOUT_COMPOSE_CONFIRM_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
  });
}

/** POST /scout/compose/preview - full composed spec for pre-launch display (pure read). */
export function usePreview() {
  return useMutation<PreviewResponse, ApiError, { composed: ComposeResponse['composed'] }>({
    mutationFn: (payload) =>
      apiV2Fetch<PreviewResponse>(SCOUT_COMPOSE_PREVIEW_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify({ composed: payload.composed }),
      }),
  });
}

/**
 * POST /scout/compose/launch - record snapshot + execute the pipeline.
 *
 * CONSTRAINT: this is ONLY called from the preview screen after the operator
 * has confirmed all loose roles. The backend rejects unconfirmed specs (409),
 * but the UI must not offer a path to launch that skips the preview step.
 */
export function useLaunch() {
  return useMutation<LaunchResponse, ApiError, LaunchRequest>({
    mutationFn: (payload) =>
      apiV2Fetch<LaunchResponse>(SCOUT_COMPOSE_LAUNCH_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
  });
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Run status (P17)                                                         */
/* ─────────────────────────────────────────────────────────────────────── */

const RUN_POLL_INTERVAL_MS = 5000;

/** GET /scout/runs - run history, polls while any run is still in flight. */
export function useScoutRuns() {
  return useQuery<RunSummary[], ApiError>({
    queryKey: scoutQueryKeys.runs,
    queryFn: async () => (await apiV2Fetch<RunsListResponse>(SCOUT_RUNS_ENDPOINT)).runs,
    refetchInterval: (query) =>
      query.state.data?.some((run) => run.status === 'running') ? RUN_POLL_INTERVAL_MS : false,
  });
}

/** GET /scout/runs/:runId - one run, polls while the run is still in flight. */
export function useScoutRun(runId: string) {
  return useQuery<RunDetail, ApiError>({
    queryKey: scoutQueryKeys.run(runId),
    queryFn: () => apiV2Fetch<RunDetail>(scoutRunDetailEndpoint(runId)),
    refetchInterval: (query) =>
      query.state.data?.status === 'running' ? RUN_POLL_INTERVAL_MS : false,
  });
}

/**
 * POST /scout/runs/:runId/resume - resume a FAILED run from its checkpoints
 * (P26). Wraps the P07 resume lane (`pnpm scout:resume`) - one lane, two
 * interfaces. Checkpointed stages (incl. stage-7 contact routing) are never
 * re-run, so a resume never re-spends provider credits. Only offered on
 * failed runs; the backend rejects running/completed with 409.
 */
export function useResumeRun(runId: string) {
  const queryClient = useQueryClient();
  return useMutation<LaunchResponse, ApiError, void>({
    mutationFn: () => apiV2Fetch<LaunchResponse>(scoutRunResumeEndpoint(runId), { method: 'POST' }),
    onSettled: () => {
      // Refresh the run + list either way - a failed resume still moved the
      // status through 'running' back to 'failed'.
      void queryClient.invalidateQueries({ queryKey: scoutQueryKeys.run(runId) });
      void queryClient.invalidateQueries({ queryKey: scoutQueryKeys.runs });
    },
  });
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Human QA gate (P18) — one lane, two interfaces (CLI + UI)                */
/* ─────────────────────────────────────────────────────────────────────── */

/** GET /scout/qa/:runId/queue — the per-run QA queue (graded leads + review state). */
export function useQaQueue(runId: string) {
  return useQuery<QaQueueResponse, ApiError>({
    queryKey: scoutQueryKeys.qaQueue(runId),
    queryFn: () => apiV2Fetch<QaQueueResponse>(scoutQaQueueEndpoint(runId)),
  });
}

/**
 * POST /scout/qa/:runId/decision — submit one lead's decision.
 *
 * OVERRIDE-REQUIRES-REASON: the backend rejects an override (finalGrade !==
 * aiGrade) without a non-empty note with 400 { code: 'override-requires-reason' }.
 * The UI must not offer a submit path that bypasses the reason field on override.
 * On success the queue cache is invalidated so the reviewed count updates.
 */
export function useSubmitQaDecision(runId: string) {
  const queryClient = useQueryClient();
  return useMutation<QaDecisionResponse, ApiError, QaDecisionRequest>({
    mutationFn: (payload) =>
      apiV2Fetch<QaDecisionResponse>(scoutQaDecisionEndpoint(runId), {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      // Invalidate the queue + summary so the reviewed count + session state update.
      void queryClient.invalidateQueries({ queryKey: ['scout', 'qa', runId] });
    },
  });
}

/** GET /scout/qa/:runId/summary — session summary (counts, overrides, guard-flagged rules). */
export function useQaSummary(runId: string) {
  return useQuery<QaSessionSummaryResponse, ApiError>({
    queryKey: scoutQueryKeys.qaSummary(runId),
    queryFn: () => apiV2Fetch<QaSessionSummaryResponse>(scoutQaSummaryEndpoint(runId)),
  });
}

/**
 * POST /scout/suppressions/:id/promote — §1.6 human-approval gate. Promotes a
 * QA-created 'pending_review' rule (held by the P06 volume guard) to 'active'.
 * Backend: 404 cross-org, 409 when the rule is not pending_review. On success
 * the QA summary + suppressions caches are invalidated so the pending badge
 * flips to active without a manual reload. On 409 the summary is also
 * invalidated — the rule is no longer pending, so the stale row must resync.
 */
export function usePromoteSuppression(runId: string) {
  const queryClient = useQueryClient();
  return useMutation<PromotedSuppression, ApiError, string>({
    mutationFn: (suppressionId) =>
      apiV2Fetch<PromotedSuppression>(scoutSuppressionPromoteEndpoint(suppressionId), { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: scoutQueryKeys.qaSummary(runId) });
      void queryClient.invalidateQueries({ queryKey: scoutQueryKeys.suppressions });
    },
    onError: (error) => {
      if (error.status === 409) {
        void queryClient.invalidateQueries({ queryKey: scoutQueryKeys.qaSummary(runId) });
      }
    },
  });
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Export screen (P19) — one lane, two interfaces (CLI + UI)                */
/* The export UI wraps the same P03 export lane the CLI uses. Write-back     */
/* semantics preserved: export does NOT re-write touch_history clocks;      */
/* re-export is idempotent (identical CSV + upserted history row).          */
/* ─────────────────────────────────────────────────────────────────────── */

/** GET /scout/export/:runId/preview?scope=ab|cd — pre-download column preview. */
export function useExportPreview(runId: string, scope: ExportScope) {
  return useQuery<ExportPreviewResponse, ApiError>({
    queryKey: scoutQueryKeys.exportPreview(runId, scope),
    queryFn: () => apiV2Fetch<ExportPreviewResponse>(scoutExportPreviewEndpoint(runId, scope)),
    // The export panel mounts before a run is selected — don't fire with ''.
    enabled: runId.length > 0,
  });
}

/** POST /scout/export/:runId/generate?scope=ab|cd — generate/re-generate the CSV. */
export function useGenerateExport(runId: string, scope: ExportScope) {
  const queryClient = useQueryClient();
  return useMutation<ExportGenerateResponse, ApiError, void>({
    mutationFn: () => apiV2Fetch<ExportGenerateResponse>(scoutExportGenerateEndpoint(runId, scope), { method: 'POST' }),
    onSuccess: () => {
      // Invalidate the history so the audit trail updates after generate.
      void queryClient.invalidateQueries({ queryKey: ['scout', 'export', runId] });
    },
  });
}

/** GET /scout/export/:runId/history — export audit trail (who/when/scope/rows). */
export function useExportHistory(runId: string) {
  return useQuery<ExportHistoryResponse, ApiError>({
    queryKey: scoutQueryKeys.exportHistory(runId),
    queryFn: () => apiV2Fetch<ExportHistoryResponse>(scoutExportHistoryEndpoint(runId)),
    // The export panel mounts before a run is selected — don't fire with ''.
    enabled: runId.length > 0,
  });
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Workspace always-on suppression lists (P20) — §1.3 layer 1.              */
/* Bulk paste domains/emails into typed lists. Competitor entries are       */
/* review-first (locked nuance). Same suppressions JSONB the P14 profile    */
/* route owns; entries take effect at compose time (P15 merge).             */
/* ─────────────────────────────────────────────────────────────────────── */

/** GET /scout/suppressions — all always-on suppression lists. */
export function useScoutSuppressions() {
  return useQuery<SuppressionLists, ApiError>({
    queryKey: scoutQueryKeys.suppressions,
    queryFn: () => apiV2Fetch<SuppressionLists>(SCOUT_SUPPRESSIONS_ENDPOINT),
  });
}

/** POST /scout/suppressions/add — bulk add to one typed list. */
export function useAddSuppressions() {
  const queryClient = useQueryClient();
  return useMutation<SuppressionLists, ApiError, SuppressionAddRequest>({
    mutationFn: (payload) =>
      apiV2Fetch<SuppressionLists>(SCOUT_SUPPRESSIONS_ADD_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: (lists) => {
      queryClient.setQueryData<SuppressionLists>(scoutQueryKeys.suppressions, lists);
    },
  });
}

/** POST /scout/suppressions/remove — remove entries from one typed list. */
export function useRemoveSuppressions() {
  const queryClient = useQueryClient();
  return useMutation<SuppressionRemoveResponse, ApiError, SuppressionRemoveRequest>({
    mutationFn: (payload) =>
      apiV2Fetch<SuppressionRemoveResponse>(SCOUT_SUPPRESSIONS_REMOVE_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: (res) => {
      const { removed: _removed, ...lists } = res;
      void _removed;
      queryClient.setQueryData<SuppressionLists>(scoutQueryKeys.suppressions, lists);
    },
  });
}

/** PUT /scout/suppressions/:type — replace one typed list outright. */
export function useReplaceSuppressions() {
  const queryClient = useQueryClient();
  return useMutation<SuppressionLists, ApiError, { type: SuppressionListType; payload: SuppressionReplaceRequest }>({
    mutationFn: ({ type, payload }) =>
      apiV2Fetch<SuppressionLists>(scoutSuppressionTypeEndpoint(type), {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),
    onSuccess: (lists) => {
      queryClient.setQueryData<SuppressionLists>(scoutQueryKeys.suppressions, lists);
    },
  });
}

/** DELETE /scout/suppressions/:type — clear one typed list. */
export function useClearSuppressions() {
  const queryClient = useQueryClient();
  return useMutation<SuppressionClearResponse, ApiError, SuppressionListType>({
    mutationFn: (type) =>
      apiV2Fetch<SuppressionClearResponse>(scoutSuppressionTypeEndpoint(type), { method: 'DELETE' }),
    onSuccess: (res) => {
      const { cleared: _cleared, ...lists } = res;
      void _cleared;
      queryClient.setQueryData<SuppressionLists>(scoutQueryKeys.suppressions, lists);
    },
  });
}